/**
 * Painel GM — Mercado de ALZ (RMT). 2026-09-26.
 *
 * Abas: Visão geral · Solicitações · Anúncios · Vendas · Vendedores · Configuração.
 * So' ALZ: a parte de ITENS (liga/desliga venda de itens, faixa de preco de
 * item, blacklist e limite de preco por item) saiu da tela e ficou COMENTADA no
 * fim deste arquivo. Os valores continuam salvos como estavam (o PUT /config
 * reenvia o que veio do banco).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HandCoins, Loader2, AlertCircle, Search, X, Trash2, RefreshCw, Vault, UserPlus, Pencil,
  LayoutDashboard, FileCheck2, Store, ReceiptText, Users, Settings2, CheckCircle2, XCircle,
  FileText, Smartphone, Undo2, EyeOff, KeyRound, Send, BadgeCheck, Ban, FlaskConical, QrCode,
  Percent, TriangleAlert, Coins, TrendingUp, Wallet, Clock, ShieldCheck, Info,
} from 'lucide-react';
import {
  getRmtConfig, updateRmtConfig, setRmtCofre, searchRmtChar, runRmtScan,
  listRmtSellers, searchRmtAccount, grantRmtSeller, updateRmtSeller, revokeRmtSeller,
  getRmtApplications, approveRmtApplication, rejectRmtApplication, fetchRmtDoc,
  getRmtMetrics, getRmtAdminOrders, rmtOrderPayout, rmtOrderPayoutManual, rmtOrderCancel,
  getRmtListings, rmtDelist, rmtReturnEscrow, rmtSetPix, getRmtMarketConfig, putRmtMarketConfig,
  type RmtConfig, type RmtSeller, type RmtAccountSearch, type RmtCharSearch,
  type RmtSellLimits, type RmtItemConfig, type RmtApplication, type RmtMetrics,
  type RmtAdminOrder, type RmtListing, type RmtMarketConfig,
} from '@/shared/services/rmtService';
import { useToast } from '@/shared/contexts/ToastContext';
import { BarChart } from '@/shared/components/rmt/RmtCharts';
import '@/shared/components/rmt/rmt.css';

const fmt = (n: number) => (n ?? 0).toLocaleString('pt-BR');
const fmtBrl = (n: number) =>
  'R$ ' + (n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString('pt-BR') : '—');
function alzShort(n: number): string {
  const t = (x: number) => Number(x.toFixed(2)).toString();
  if (!n) return '0';
  if (Math.abs(n) >= 1e9) return t(n / 1e9) + 'B';
  if (Math.abs(n) >= 1e6) return t(n / 1e6) + 'kk';
  return n.toLocaleString('pt-BR');
}
const errMsg = (e: any, fb = 'Erro') =>
  (typeof e?.detail === 'string' ? e.detail : e?.detail?.Message) || e?.message || fb;

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-500/15 text-emerald-400',
  suspended: 'bg-red-500/15 text-red-400',
};
const DOC_BADGE: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  rejected: 'bg-red-500/15 text-red-400',
};
const DOC_LABEL: Record<string, string> = {
  pending: 'Doc. pendente', approved: 'Doc. aprovada', rejected: 'Doc. rejeitada',
};
const ORDER_ST: Record<string, { l: string; c: string }> = {
  completed: { l: 'Concluído', c: 'bg-emerald-500/15 text-emerald-400' },
  awaiting_payment: { l: 'Aguardando Pix', c: 'bg-amber-500/15 text-amber-300' },
  expired: { l: 'Vencido', c: 'bg-white/10 text-white/50' },
  cancelled: { l: 'Cancelado', c: 'bg-white/10 text-white/50' },
  paid_no_stock: { l: 'Pago sem ALZ', c: 'bg-red-500/20 text-red-300' },
};
const PAYOUT_ST: Record<string, { l: string; c: string }> = {
  none: { l: '—', c: 'bg-white/5 text-white/35' },
  pending: { l: 'Na fila', c: 'bg-amber-500/15 text-amber-300' },
  sending: { l: 'Enviando', c: 'bg-sky-500/15 text-sky-300' },
  sent: { l: 'Enviado', c: 'bg-sky-500/15 text-sky-300' },
  done: { l: 'Pago', c: 'bg-emerald-500/15 text-emerald-400' },
  manual: { l: 'Pago por fora', c: 'bg-emerald-500/15 text-emerald-400' },
  failed: { l: 'Falhou', c: 'bg-red-500/15 text-red-300' },
};
const PIX_LABEL: Record<string, string> = { cpf: 'CPF', cnpj: 'CNPJ', email: 'E-mail', phone: 'Celular', random: 'Aleatória' };

type Tab = 'overview' | 'apps' | 'listings' | 'orders' | 'sellers' | 'config';

function LimitField({ label, value, onChange, step }: {
  label: string; value: number; onChange: (v: number) => void; step?: string;
}) {
  return (
    <div>
      <label className="text-[11px] text-white/55 block mb-1">{label}</label>
      {/* value vazio no 0: senao o <input number> controlado mantem o "0" a esquerda do que se digita */}
      <input type="number" min={0} step={step} value={value || ''} placeholder="0" onFocus={(e) => e.target.select()}
             onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))} className="rmt-input !py-2" />
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent, onClick }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string; onClick?: () => void;
}) {
  return (
    <div onClick={onClick} className={`rmt-kpi rounded-xl p-4 min-w-0 ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45">
        <span className={accent || 'text-[#8fb9de]'}>{icon}</span>{label}
      </div>
      <div className="text-xl font-black text-white mt-1 truncate tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-white/45 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Toggle({ on, onChange, disabled, warn }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; warn?: boolean }) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!on)}
            className={`rmt-switch ${on ? 'is-on' : ''} ${warn ? 'is-warn' : ''} ${disabled ? 'opacity-40 !cursor-not-allowed' : ''}`} />
  );
}

/** Linha de config: titulo + switch + explicacao do que o checkbox faz. */
function Flag({ title, on, onChange, disabled, warn, children, lock }: {
  title: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean; warn?: boolean;
  children: React.ReactNode; lock?: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl border p-4 ${on ? 'border-[#f0c86f]/25 bg-[#f0c86f]/[0.03]' : 'border-white/[0.07] bg-black/20'}`}>
      <div className="flex items-center gap-3">
        <p className="text-sm font-black text-white flex-1">{title}</p>
        <span className={`text-[10px] font-black uppercase ${on ? 'rmt-gold' : 'text-white/35'}`}>{on ? 'ligado' : 'desligado'}</span>
        <Toggle on={on} onChange={onChange} disabled={disabled} warn={warn} />
      </div>
      <div className="text-[12px] text-white/55 mt-2 leading-relaxed space-y-1">{children}</div>
      {lock && <div className="text-[11px] text-amber-300 mt-2 flex items-start gap-1.5"><TriangleAlert size={12} className="shrink-0 mt-0.5" />{lock}</div>}
    </div>
  );
}

/* ════════════════════════════ VISÃO GERAL ════════════════════════════ */

function OverviewTab({ go }: { go: (t: Tab) => void }) {
  const [m, setM] = useState<RmtMetrics | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [serie, setSerie] = useState<'brl' | 'alz' | 'trades'>('brl');
  useEffect(() => { getRmtMetrics().then(setM).catch(e => setErr(errMsg(e))); }, []);
  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!m) return <div className="py-16 flex justify-center text-white/40"><Loader2 className="animate-spin" /></div>;

  const pts = m.series.map(d => {
    const label = new Date(d.day + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    const value = serie === 'brl' ? d.brl : serie === 'alz' ? d.alz : d.trades;
    return { label, value, hint: `${d.trades} venda(s)` };
  });
  const fmtSerie = serie === 'brl' ? fmtBrl : serie === 'alz' ? (v: number) => alzShort(v) + ' ALZ' : (v: number) => `${v} venda(s)`;
  const alerts = [
    m.pendingApplications > 0 && { t: `${m.pendingApplications} solicitação(ões) de vendedor aguardando`, tab: 'apps' as Tab, c: 'text-amber-300' },
    m.payoutFailed > 0 && { t: `${m.payoutFailed} repasse(s) Pix com falha`, tab: 'orders' as Tab, c: 'text-red-300' },
    m.paidNoStock > 0 && { t: `${m.paidNoStock} pedido(s) pago(s) sem ALZ — devolver o dinheiro`, tab: 'orders' as Tab, c: 'text-red-300' },
    m.awaitingOrders > 0 && { t: `${m.awaitingOrders} pedido(s) aguardando Pix (${alzShort(m.reservedAlz)} ALZ reservado)`, tab: 'orders' as Tab, c: 'text-white/70' },
  ].filter(Boolean) as { t: string; tab: Tab; c: string }[];

  return (
    <div className="space-y-5">
      {alerts.length > 0 && (
        <div className="rmt-card rounded-xl p-4 space-y-1.5">
          {alerts.map((a, i) => (
            <button key={i} onClick={() => go(a.tab)} className={`flex items-center gap-2 text-sm ${a.c} hover:underline cursor-pointer`}>
              <TriangleAlert size={14} /> {a.t}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<Percent size={12} />} label="Nossa comissão" value={fmtBrl(m.realCommission)}
             sub={`30d: ${fmtBrl(m.commission30d)}`} accent="rmt-gold" />
        <Kpi icon={<TrendingUp size={12} />} label="Faturado (Pix real)" value={fmtBrl(m.realBrl)}
             sub={`${m.realTrades} venda(s) paga(s)`} accent="rmt-up" />
        <Kpi icon={<Coins size={12} />} label="ALZ negociado" value={alzShort(m.totalAlz)}
             sub={`${m.totalTrades} venda(s) · ${m.sandboxTrades} sandbox`} />
        <Kpi icon={<Wallet size={12} />} label="A repassar" value={fmtBrl(m.payoutPendingBrl)}
             sub={m.payoutFailed ? `${m.payoutFailed} com falha` : 'aos vendedores'} onClick={() => go('orders')} />
        <Kpi icon={<Vault size={12} />} label="Em custódia" value={alzShort(m.escrowAlz)}
             sub={`${alzShort(m.deposits30dAlz)} depositado 30d`} />
        <Kpi icon={<Store size={12} />} label="Anunciado" value={alzShort(m.listedAlz)}
             sub={`${m.sellersListing} oferta(s) · melhor ${m.bestPrice ? fmtBrl(m.bestPrice) : '—'}`} onClick={() => go('listings')} />
        <Kpi icon={<Users size={12} />} label="Vendedores ativos" value={m.sellersActive}
             sub={`${m.pendingApplications} solicitação(ões)`} onClick={() => go('apps')} />
        <Kpi icon={<Clock size={12} />} label="Preço médio" value={m.avgPrice ? fmtBrl(m.avgPrice) : '—'}
             sub={`30d: ${m.trades30d} venda(s) · ${fmtBrl(m.brl30d)}`} />
      </div>

      <div className="rmt-card rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h3 className="text-sm font-black text-white uppercase tracking-wider">Últimos 30 dias</h3>
          {serie === 'brl' && <span className="text-[11px] text-white/45"><span className="rmt-gold">■</span> faturamento · <span className="text-emerald-400">■</span> comissão</span>}
          <div className="ml-auto flex rounded-lg bg-white/5 p-0.5">
            {([['brl', 'R$'], ['alz', 'ALZ'], ['trades', 'Vendas']] as const).map(([k, l]) => (
              <button key={k} onClick={() => setSerie(k)}
                      className={`px-3 py-1 rounded-md text-xs font-bold cursor-pointer ${serie === k ? 'bg-[#f0c86f]/20 text-[#f0c86f]' : 'text-white/50'}`}>{l}</button>
            ))}
          </div>
        </div>
        <BarChart points={pts} format={fmtSerie} height={200}
                  secondary={serie === 'brl' ? { values: m.series.map(d => d.commission), color: '#34d399', label: 'Comissão' } : undefined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {[['Top vendedores (30d)', m.topSellers], ['Top compradores (30d)', m.topBuyers]].map(([title, rows]) => (
          <div key={title as string} className="rmt-card rounded-2xl p-5">
            <h3 className="text-sm font-black text-white uppercase tracking-wider mb-3">{title as string}</h3>
            {(rows as typeof m.topSellers).length === 0 ? <p className="text-xs text-white/40">Sem vendas no período.</p> :
              (rows as typeof m.topSellers).map((r, i) => (
                <div key={r.userNum} className="flex items-center gap-3 py-2 border-t border-white/[0.04] first:border-0">
                  <span className={`w-6 text-center font-black ${i === 0 ? 'rmt-gold' : 'text-white/40'}`}>{i + 1}</span>
                  <span className="text-sm text-white font-bold flex-1 truncate">{r.name || `UserNum ${r.userNum}`}</span>
                  <span className="text-xs text-white/50 tabular-nums">{alzShort(r.alz)} ALZ</span>
                  <span className="text-sm font-black rmt-gold tabular-nums w-24 text-right">{fmtBrl(r.brl)}</span>
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════ SOLICITAÇÕES ════════════════════════════ */

function ApplicationsTab() {
  const { notify } = useToast();
  const [rows, setRows] = useState<RmtApplication[] | null>(null);
  const [filter, setFilter] = useState<'pending' | ''>('pending');
  const [busy, setBusy] = useState<number | null>(null);
  const load = useCallback(() => {
    getRmtApplications(filter || undefined).then(setRows).catch(e => notify('error', errMsg(e)));
  }, [filter, notify]);
  useEffect(() => { load(); }, [load]);

  const openDoc = async (a: RmtApplication, f: string) => {
    try { window.open(await fetchRmtDoc(a.applicationId, f), '_blank'); }
    catch (e: any) { notify('error', errMsg(e, 'Arquivo indisponível')); }
  };
  const approve = async (a: RmtApplication) => {
    if (!confirm(`Aprovar ${a.sellerName} (${a.accountId}) como vendedor de ALZ?`)) return;
    setBusy(a.applicationId);
    try { await approveRmtApplication(a.applicationId); notify('success', 'Vendedor aprovado'); load(); }
    catch (e: any) { notify('error', errMsg(e)); } finally { setBusy(null); }
  };
  const reject = async (a: RmtApplication) => {
    const reason = prompt('Motivo da recusa (o jogador vê):');
    if (!reason || reason.trim().length < 3) return;
    setBusy(a.applicationId);
    try { await rejectRmtApplication(a.applicationId, reason.trim()); notify('success', 'Solicitação recusada'); load(); }
    catch (e: any) { notify('error', errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {([['pending', 'Pendentes'], ['', 'Todas']] as const).map(([k, l]) => (
          <button key={l} onClick={() => setFilter(k)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold cursor-pointer ${filter === k ? 'bg-[#f0c86f]/20 text-[#f0c86f]' : 'bg-white/5 text-white/55'}`}>{l}</button>
        ))}
      </div>
      {!rows ? <div className="py-12 flex justify-center text-white/40"><Loader2 className="animate-spin" /></div>
        : rows.length === 0 ? <div className="rmt-card rounded-2xl py-14 text-center text-white/40 text-sm">Nenhuma solicitação.</div>
          : rows.map(a => (
            <div key={a.applicationId} className="rmt-card rounded-2xl p-5">
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-lg font-black text-white">{a.sellerName}</p>
                    <span className={`rmt-pill ${DOC_BADGE[a.status]}`}>{a.status === 'pending' ? 'pendente' : a.status === 'approved' ? 'aprovada' : 'recusada'}</span>
                    <span className="text-[11px] text-white/35 font-mono">#{a.applicationId}</span>
                  </div>
                  <p className="text-xs text-white/55 mt-1 font-mono">
                    conta <b className="text-white">{a.accountId}</b> · UserNum {a.userNum} · {fmtDate(a.createdAt)}
                  </p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm">
                    <span className="text-white/60 flex items-center gap-1.5"><KeyRound size={13} className="text-[#8fb9de]" />
                      Pix {PIX_LABEL[a.pixKeyType] || a.pixKeyType}: <b className="text-white font-mono">{a.pixKey}</b></span>
                    <span className={`flex items-center gap-1.5 ${a.hasCellphone ? 'text-emerald-400' : 'text-red-300'}`}>
                      <Smartphone size={13} />{a.hasCellphone ? 'celular vinculado' : 'sem celular'}</span>
                    <span className="text-white/55">{a.charCount} char(s) · nível máx {a.maxLevel}</span>
                    {a.accountCreated && <span className="text-white/45">conta de {new Date(a.accountCreated).toLocaleDateString('pt-BR')}</span>}
                  </div>
                  {a.docFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {a.docFiles.map(f => (
                        <button key={f} onClick={() => openDoc(a, f)}
                                className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-white/80 flex items-center gap-1.5 cursor-pointer">
                          <FileText size={12} className="text-[#8fb9de]" /> {f}
                        </button>
                      ))}
                    </div>
                  )}
                  {a.rejectReason && <p className="text-xs text-red-300 mt-2">Motivo: {a.rejectReason}</p>}
                  {a.reviewedById && <p className="text-[11px] text-white/35 mt-1">analisado por {a.reviewedById} em {fmtDate(a.reviewedAt)}</p>}
                </div>
                {a.status === 'pending' && (
                  <div className="flex gap-2">
                    <button onClick={() => reject(a)} disabled={busy === a.applicationId}
                            className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm font-bold flex items-center gap-1.5 cursor-pointer">
                      <XCircle size={14} /> Recusar
                    </button>
                    <button onClick={() => approve(a)} disabled={busy === a.applicationId}
                            className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#04140c] text-sm font-black flex items-center gap-1.5 cursor-pointer">
                      {busy === a.applicationId ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Aprovar
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
    </div>
  );
}

/* ════════════════════════════ ANÚNCIOS ════════════════════════════ */

function ListingsTab() {
  const { notify } = useToast();
  const [rows, setRows] = useState<RmtListing[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const load = useCallback(() => { getRmtListings().then(setRows).catch(e => notify('error', errMsg(e))); }, [notify]);
  useEffect(() => { load(); }, [load]);

  const act = async (u: number, fn: () => Promise<unknown>, ok: string) => {
    setBusy(u);
    try { await fn(); notify('success', ok); load(); } catch (e: any) { notify('error', errMsg(e)); } finally { setBusy(null); }
  };
  const delist = (r: RmtListing) => {
    if (!confirm(`Tirar do ar a oferta de ${r.sellerName || r.accountId}? O ALZ continua na custódia.`)) return;
    act(r.userNum, () => rmtDelist(r.userNum), 'Oferta retirada');
  };
  const giveBack = (r: RmtListing) => {
    const reason = prompt(`DEVOLVER ${fmt(r.balanceAlz)} ALZ para ${r.sellerName || r.accountId} pelo correio e zerar a custódia?\nMotivo (opcional, vai no correio):`);
    if (reason === null) return;
    act(r.userNum, () => rmtReturnEscrow(r.userNum, reason.trim() || undefined), 'ALZ devolvido pelo correio');
  };
  const editPix = (r: RmtListing) => {
    const type = prompt('Tipo da chave (cpf, cnpj, email, phone, random):', r.pixKeyType || 'cpf');
    if (!type) return;
    const key = prompt('Nova chave Pix:', r.pixKey || '');
    if (!key) return;
    act(r.userNum, () => rmtSetPix(r.userNum, key.trim(), type.trim().toLowerCase()), 'Chave Pix atualizada');
  };

  const totals = useMemo(() => (rows || []).reduce((a, r) => ({
    bal: a.bal + r.balanceAlz, av: a.av + r.availableAlz, res: a.res + r.reservedAlz,
  }), { bal: 0, av: 0, res: 0 }), [rows]);

  if (!rows) return <div className="py-12 flex justify-center text-white/40"><Loader2 className="animate-spin" /></div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Kpi icon={<Vault size={12} />} label="Custódia total" value={alzShort(totals.bal)} accent="rmt-gold" />
        <Kpi icon={<Store size={12} />} label="Anunciado" value={alzShort(totals.av)} />
        <Kpi icon={<Clock size={12} />} label="Reservado (Pix)" value={alzShort(totals.res)} />
      </div>
      <div className="rmt-card rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-white/40 text-left">
              <th className="px-4 py-3">Vendedor</th><th className="px-3">Custódia</th><th className="px-3">Anunciado</th>
              <th className="px-3">Preço /100kk</th><th className="px-3">Reservado</th><th className="px-3">Vendeu</th>
              <th className="px-3">Pix</th><th className="px-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-white/40">Nenhum vendedor.</td></tr>}
            {rows.map(r => (
              <tr key={r.userNum} className="rmt-row border-t border-white/[0.04]">
                <td className="px-4 py-3">
                  <p className="font-bold text-white">{r.sellerName || '—'}</p>
                  <p className="text-[11px] text-white/40 font-mono">{r.accountId} · {r.userNum}
                    <span className={`ml-2 rmt-pill ${STATUS_BADGE[r.status] || 'bg-white/10 text-white/50'}`}>{r.status === 'active' ? 'ativo' : 'suspenso'}</span></p>
                </td>
                <td className="px-3 font-black rmt-gold tabular-nums">{alzShort(r.balanceAlz)}</td>
                <td className="px-3 tabular-nums text-white">{alzShort(r.availableAlz)}</td>
                <td className="px-3 tabular-nums text-white">{r.alzPriceBrl ? fmtBrl(r.alzPriceBrl) : '—'}</td>
                <td className="px-3 tabular-nums text-white/60">{r.reservedAlz ? alzShort(r.reservedAlz) : '—'}</td>
                <td className="px-3 text-white/60"><span className="tabular-nums">{alzShort(r.soldAlz)}</span><br /><span className="text-[11px]">{fmtBrl(r.soldBrl)} · {r.trades}x</span></td>
                <td className="px-3 text-[11px] text-white/60 font-mono max-w-[160px] truncate">{r.pixKey ? `${PIX_LABEL[r.pixKeyType || ''] || ''} ${r.pixKey}` : <span className="text-red-300">sem chave</span>}</td>
                <td className="px-4">
                  <div className="flex justify-end gap-1.5">
                    {busy === r.userNum ? <Loader2 size={15} className="animate-spin text-white/50" /> : <>
                      <button onClick={() => editPix(r)} title="Trocar chave Pix" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white cursor-pointer"><KeyRound size={13} /></button>
                      <button onClick={() => delist(r)} disabled={!r.availableAlz} title="Tirar do ar" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white cursor-pointer disabled:opacity-30"><EyeOff size={13} /></button>
                      <button onClick={() => giveBack(r)} disabled={!r.balanceAlz} title="Devolver o ALZ pelo correio" className="px-2.5 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-bold flex items-center gap-1 cursor-pointer disabled:opacity-30"><Undo2 size={13} /> Devolver</button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════════════════ VENDAS ════════════════════════════ */

function OrdersTab() {
  const { notify } = useToast();
  const [rows, setRows] = useState<RmtAdminOrder[] | null>(null);
  const [st, setSt] = useState('');
  const [payout, setPayout] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const load = useCallback(() => {
    const p: { status?: string; payout?: string } = {};
    if (st) p.status = st;
    if (payout) p.payout = payout;
    getRmtAdminOrders(p).then(setRows).catch(e => notify('error', errMsg(e)));
  }, [st, payout, notify]);
  useEffect(() => { load(); }, [load]);

  const act = async (id: number, fn: () => Promise<unknown>, ok: string, ask?: string) => {
    if (ask && !confirm(ask)) return;
    setBusy(id);
    try { await fn(); notify('success', ok); load(); } catch (e: any) { notify('error', errMsg(e)); } finally { setBusy(null); }
  };
  const sum = (rows || []).filter(r => r.status === 'completed').reduce((a, r) => ({
    brl: a.brl + (r.isSandbox ? 0 : r.priceBrl), com: a.com + (r.isSandbox ? 0 : r.commissionBrl), n: a.n + 1,
  }), { brl: 0, com: 0, n: 0 });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={st} onChange={e => setSt(e.target.value)} className="rmt-input !w-auto !py-1.5 text-xs">
          <option value="">Todos os status</option>
          {Object.entries(ORDER_ST).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
        <select value={payout} onChange={e => setPayout(e.target.value)} className="rmt-input !w-auto !py-1.5 text-xs">
          <option value="">Todo repasse</option>
          {Object.entries(PAYOUT_ST).filter(([k]) => k !== 'none').map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white cursor-pointer"><RefreshCw size={13} /></button>
        <span className="ml-auto text-xs text-white/50">
          {sum.n} concluída(s) · faturado <b className="text-white">{fmtBrl(sum.brl)}</b> · comissão <b className="rmt-gold">{fmtBrl(sum.com)}</b>
        </span>
      </div>
      <div className="rmt-card rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-white/40 text-left">
              <th className="px-4 py-3">Pedido</th><th className="px-3">Comprador</th><th className="px-3">Vendedor</th>
              <th className="px-3">ALZ</th><th className="px-3">Valor</th><th className="px-3">Comissão</th>
              <th className="px-3">Status</th><th className="px-3">Repasse Pix</th><th className="px-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={9} className="py-12 text-center"><Loader2 className="animate-spin inline text-white/40" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={9} className="py-12 text-center text-white/40">Nenhuma venda.</td></tr>}
            {rows?.map(o => {
              const os = ORDER_ST[o.status] || { l: o.status, c: 'bg-white/10 text-white/50' };
              const ps = PAYOUT_ST[o.payoutStatus] || { l: o.payoutStatus, c: 'bg-white/10 text-white/50' };
              return (
                <tr key={o.orderId} className="rmt-row border-t border-white/[0.04] align-top">
                  <td className="px-4 py-3"><p className="font-mono text-white">#{o.orderId}</p><p className="text-[11px] text-white/40">{fmtDate(o.createdAt)}</p>
                    {o.isSandbox && <span className="rmt-pill bg-amber-500/10 text-amber-300 mt-1">sandbox</span>}</td>
                  <td className="px-3 py-3"><p className="text-white">{o.buyerAccount || o.buyerUserNum}</p><p className="text-[11px] text-white/40">→ {o.buyerChar}</p></td>
                  <td className="px-3 py-3 text-white/80">{o.sellerName || o.sellerUserNum || '—'}</td>
                  <td className="px-3 py-3 font-bold text-white tabular-nums">{alzShort(o.alzAmount)}</td>
                  <td className="px-3 py-3 tabular-nums text-white">{fmtBrl(o.priceBrl)}<p className="text-[11px] text-white/40">vendedor {fmtBrl(o.payoutBrl)}</p></td>
                  <td className="px-3 py-3 tabular-nums rmt-gold font-bold">{fmtBrl(o.commissionBrl)}</td>
                  <td className="px-3 py-3"><span className={`rmt-pill ${os.c}`}>{os.l}</span></td>
                  <td className="px-3 py-3">
                    <span className={`rmt-pill ${ps.c}`}>{ps.l}</span>
                    {o.payoutTries > 0 && <p className="text-[10px] text-white/35 mt-1">{o.payoutTries} tentativa(s)</p>}
                    {o.payoutError && <p className="text-[10px] text-red-300 mt-0.5 max-w-[180px]" title={o.payoutError}>{o.payoutError.slice(0, 60)}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      {busy === o.orderId ? <Loader2 size={15} className="animate-spin text-white/50" /> : <>
                        {o.status === 'completed' && !o.isSandbox && ['pending', 'failed'].includes(o.payoutStatus) && <>
                          <button onClick={() => act(o.orderId, () => rmtOrderPayout(o.orderId), 'Pix enviado', `Enviar agora ${fmtBrl(o.payoutBrl)} por Pix para ${o.sellerName}?`)}
                                  className="px-2.5 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-bold flex items-center gap-1 cursor-pointer"><Send size={12} /> Pagar</button>
                          <button onClick={() => act(o.orderId, () => rmtOrderPayoutManual(o.orderId), 'Marcado como pago', 'Marcar como pago por fora (sem enviar Pix pela API)?')}
                                  className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-xs font-bold cursor-pointer">Pago por fora</button>
                        </>}
                        {o.status === 'awaiting_payment' && (
                          <button onClick={() => act(o.orderId, () => rmtOrderCancel(o.orderId), 'Pedido cancelado', 'Cancelar o pedido e devolver o ALZ reservado ao vendedor?')}
                                  className="px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-bold flex items-center gap-1 cursor-pointer"><Ban size={12} /> Cancelar</button>
                        )}
                      </>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════════════════ VENDEDORES ════════════════════════════ */

function SellersTab() {
  const { notify } = useToast();
  const [sellers, setSellers] = useState<RmtSeller[] | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<RmtAccountSearch[]>([]);
  const [editing, setEditing] = useState<RmtSeller | null>(null);

  const load = useCallback(() => { listRmtSellers().then(setSellers).catch(e => notify('error', errMsg(e))); }, [notify]);
  useEffect(() => { load(); }, [load]);

  const doSearch = async () => {
    const q = searchTerm.trim();
    if (q.length < 2) { notify('error', 'Digite ao menos 2 caracteres'); return; }
    setSearching(true);
    try { setSearchResults(await searchRmtAccount(q)); }
    catch (e: any) { notify('error', errMsg(e)); }
    finally { setSearching(false); }
  };
  const onGrant = async (r: RmtAccountSearch) => {
    try {
      await grantRmtSeller(r.userNum);
      notify('success', `${r.accountId} habilitado como vendedor — cadastre a chave Pix em Anúncios`);
      load(); await doSearch();
    } catch (e: any) { notify('error', errMsg(e)); }
  };
  const onRevoke = async (s: RmtSeller) => {
    if (!confirm(`Revogar acesso de ${s.accountId || `UserNum ${s.userNum}`}? (com histórico ele fica suspenso)`)) return;
    try { await revokeRmtSeller(s.userNum); notify('success', 'Acesso revogado'); load(); }
    catch (e: any) { notify('error', errMsg(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="rmt-card rounded-2xl p-5">
        <p className="text-sm font-black text-white uppercase tracking-wider mb-1 flex items-center gap-2"><UserPlus size={15} className="rmt-gold" /> Habilitar direto (sem solicitação)</p>
        <p className="text-[11px] text-white/45 mb-3">O caminho normal é o jogador pedir em "Vender ALZ" e a equipe aprovar em Solicitações.</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                   placeholder="Buscar por ID, email ou UserNum..." className="rmt-input !pl-9" />
          </div>
          <button onClick={doSearch} disabled={searching}
                  className="rmt-btn-gold px-5 rounded-lg font-black text-sm cursor-pointer flex items-center gap-2">
            {searching && <Loader2 size={14} className="animate-spin" />} Buscar
          </button>
        </div>
        {searchResults.length > 0 && (
          <div className="mt-4 border border-white/[0.06] rounded-lg overflow-hidden">
            {searchResults.map(r => (
              <div key={r.userNum} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04] last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">{r.accountId}</p>
                  <p className="text-[11px] text-white/45 font-mono">UserNum {r.userNum}{r.email ? ` · ${r.email}` : ''}</p>
                </div>
                {r.isSeller ? <span className="rmt-pill bg-emerald-500/15 text-emerald-400">já é vendedor</span> : (
                  <button onClick={() => onGrant(r)} className="px-3 py-1.5 rounded-lg bg-[#8fb9de]/10 hover:bg-[#8fb9de]/20 text-[#8fb9de] text-xs font-bold flex items-center gap-1 cursor-pointer">
                    <UserPlus size={12} /> Habilitar
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rmt-card rounded-2xl overflow-hidden">
        {!sellers ? <div className="py-12 flex justify-center text-white/40"><Loader2 className="animate-spin" /></div>
          : sellers.length === 0 ? <div className="py-14 text-center text-white/40 text-sm">Nenhum vendedor cadastrado</div>
            : sellers.map(s => (
              <div key={s.userNum} className="rmt-row flex items-start gap-4 px-5 py-4 border-b border-white/[0.04] last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-white">{s.sellerName || s.accountId || `UserNum ${s.userNum}`}</p>
                    <span className={`rmt-pill ${STATUS_BADGE[s.status] || 'bg-white/10 text-white/60'}`}>{s.status === 'active' ? 'ativo' : 'suspenso'}</span>
                    <span className={`rmt-pill ${DOC_BADGE[s.docStatus] || 'bg-white/10 text-white/60'}`}>{DOC_LABEL[s.docStatus] || s.docStatus}</span>
                  </div>
                  <p className="text-[11px] text-white/45 mt-1 font-mono">
                    {s.accountId || `UserNum ${s.userNum}`} · custódia <span className="rmt-gold font-bold">{fmt(s.balanceAlz)} ALZ</span>
                    {s.grantedById && <> · liberado por {s.grantedById} em {fmtDate(s.grantedAt)}</>}
                  </p>
                  {s.pixKey && <p className="text-[11px] text-white/45 mt-0.5">Pix ({PIX_LABEL[s.pixKeyType || ''] || s.pixKeyType || '—'}): {s.pixKey}</p>}
                  {s.notes && <p className="text-[11px] text-white/65 mt-1 italic">"{s.notes}"</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(s)} title="Editar" className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/65 hover:text-white cursor-pointer"><Pencil size={14} /></button>
                  <button onClick={() => onRevoke(s)} title="Revogar" className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 cursor-pointer"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
      </div>
      {editing && <SellerEditor seller={editing} onClose={() => setEditing(null)} onSaved={async () => { load(); setEditing(null); }} />}
    </div>
  );
}

/* ════════════════════════════ CONFIGURAÇÃO ════════════════════════════ */

function ConfigTab() {
  const { notify } = useToast();
  const [mk, setMk] = useState<RmtMarketConfig | null>(null);
  const [config, setConfig] = useState<RmtConfig | null>(null);
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [minDeposit, setMinDeposit] = useState(0);
  const [limits, setLimits] = useState<RmtSellLimits>({
    limitAlzDay: 0, limitAlzWeek: 0, limitAlzMonth: 0, limitSalesDay: 0, limitSalesWeek: 0, limitSalesMonth: 0,
  });
  // itemCfg: carregado do banco e REENVIADO sem mudanca (a parte de itens esta' comentada)
  const [itemCfg, setItemCfg] = useState<RmtItemConfig>({
    itemsEnabled: false, alzPriceMinBrl: 0, alzPriceMaxBrl: 0, itemPriceMinBrl: 0, itemPriceMaxBrl: 0,
  });
  const [saving, setSaving] = useState(false);
  const [cofreOpen, setCofreOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, cfg] = await Promise.all([getRmtMarketConfig(), getRmtConfig()]);
      setMk(m); setConfig(cfg);
      setDepositEnabled(cfg.depositEnabled); setMinDeposit(cfg.minDepositAlz);
      setLimits({
        limitAlzDay: cfg.limitAlzDay, limitAlzWeek: cfg.limitAlzWeek, limitAlzMonth: cfg.limitAlzMonth,
        limitSalesDay: cfg.limitSalesDay, limitSalesWeek: cfg.limitSalesWeek, limitSalesMonth: cfg.limitSalesMonth,
      });
      setItemCfg({
        itemsEnabled: cfg.itemsEnabled, alzPriceMinBrl: cfg.alzPriceMinBrl, alzPriceMaxBrl: cfg.alzPriceMaxBrl,
        itemPriceMinBrl: cfg.itemPriceMinBrl, itemPriceMaxBrl: cfg.itemPriceMaxBrl,
      });
    } catch (e: any) { notify('error', errMsg(e)); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  if (!mk || !config) return <div className="py-12 flex justify-center text-white/40"><Loader2 className="animate-spin" /></div>;
  const set = (p: Partial<RmtMarketConfig>) => setMk(m => {
    const n = { ...(m as RmtMarketConfig), ...p };
    if (n.sandboxMode) n.testMode = true;   // TRAVA: sandbox ligado forca o modo teste
    return n;
  });

  const save = async () => {
    if (!mk.testMode && mk.sandboxMode) { notify('error', 'Desligue o Sandbox antes de desligar o Modo Teste.'); return; }
    if (!mk.testMode && !confirm('Desligar o Modo Teste libera o Mercado de ALZ para TODOS os jogadores, com pagamento Pix real. Confirmar?')) return;
    setSaving(true);
    try {
      const [m] = await Promise.all([
        putRmtMarketConfig(mk),
        updateRmtConfig({ depositEnabled, minDepositAlz: minDeposit, ...limits, ...itemCfg }),
      ]);
      setMk(m);
      notify('success', 'Configuração salva');
    } catch (e: any) { notify('error', errMsg(e)); await load(); }
    finally { setSaving(false); }
  };

  const example = 100 * mk.unitPriceBrl > 0 ? mk.unitPriceBrl : 5;
  return (
    <div className="space-y-5">
      {/* ─── MODO DO MERCADO ─── */}
      <div className="rmt-card rounded-2xl p-5">
        <h3 className="text-sm font-black text-white uppercase tracking-wider mb-1 flex items-center gap-2"><ShieldCheck size={15} className="rmt-gold" /> Modo do mercado</h3>
        <p className="text-[11px] text-white/45 mb-4">Combinações: Teste+Sandbox = GMs testam de graça · Teste sem Sandbox = só GMs, pagando Pix de verdade · sem Teste e sem Sandbox = aberto a todos.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Flag title="Modo Teste" on={mk.testMode} onChange={v => set({ testMode: v })} disabled={mk.sandboxMode}
                lock={mk.sandboxMode ? 'Travado: com o Sandbox ligado o Modo Teste não pode ser desligado (a compra seria grátis pra todo mundo).' : undefined}>
            <p><b className="text-white/80">Ligado:</b> "Mercado de ALZ" e "Vender ALZ" só aparecem — e só funcionam — para contas GM. Jogador comum não vê nada (a API também barra).</p>
            <p><b className="text-white/80">Desligado:</b> o mercado abre para todos os jogadores.</p>
          </Flag>
          <Flag title="Sandbox (pagamento)" on={mk.sandboxMode} onChange={v => set({ sandboxMode: v })} warn
                lock={!mk.sandboxMode && !mk.efiReady ? 'A EFI não está configurada/ativa — sem ela não dá para cobrar. Configure em Gateways de pagamento.' : undefined}>
            <p><b className="text-white/80">Ligado:</b> "Comprar" entrega o ALZ na hora, <b>sem cobrar nada</b> — só para teste. Nenhum Pix é enviado ao vendedor.</p>
            <p><b className="text-white/80">Desligado:</b> a compra gera um <b>QR Pix (EFI)</b>; pago, o ALZ vai pelo correio e a API manda o Pix ao vendedor já sem a comissão.</p>
          </Flag>
          <Flag title="Repasse automático ao vendedor" on={mk.payoutAuto} onChange={v => set({ payoutAuto: v })}>
            <p><b className="text-white/80">Ligado:</b> assim que o Pix do comprador cai, a API envia o Pix do vendedor sozinha (re-tenta até 5x se falhar).</p>
            <p><b className="text-white/80">Desligado:</b> o repasse fica "Na fila" e um GM paga pela aba Vendas (botão Pagar).</p>
          </Flag>
          <div className="rounded-xl border border-white/[0.07] bg-black/20 p-4">
            <p className="text-sm font-black text-white flex items-center gap-2"><Percent size={14} className="rmt-gold" /> Comissão da casa</p>
            <div className="flex items-center gap-2 mt-2">
              <input type="number" min={0} max={90} step="0.5" value={mk.commissionPct || ''} placeholder="0" onFocus={e => e.target.select()}
                     onChange={e => set({ commissionPct: Math.min(90, Math.max(0, Number(e.target.value) || 0)) })} className="rmt-input !w-28 text-lg font-black" />
              <span className="text-white/60 font-bold">%</span>
            </div>
            <p className="text-[12px] text-white/55 mt-2">
              Descontada de cada venda antes do repasse. Ex.: venda de {fmtBrl(example)} → vendedor recebe <b className="rmt-up">{fmtBrl(example * (1 - mk.commissionPct / 100))}</b>, casa fica com <b className="rmt-gold">{fmtBrl(example * mk.commissionPct / 100)}</b>.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          <LimitField label="Unidade de venda (ALZ)" value={mk.unitAlz} onChange={v => set({ unitAlz: v })} />
          <LimitField label="Preço de referência /unidade (R$)" value={mk.unitPriceBrl} step="0.01" onChange={v => set({ unitPriceBrl: v })} />
          <LimitField label="Validade do QR Pix (seg)" value={mk.pixExpireSec} onChange={v => set({ pixExpireSec: v })} />
        </div>
      </div>

      {/* ─── REQUISITOS DO VENDEDOR ─── */}
      <div className="rmt-card rounded-2xl p-5">
        <h3 className="text-sm font-black text-white uppercase tracking-wider mb-4 flex items-center gap-2"><BadgeCheck size={15} className="rmt-gold" /> Requisitos para virar vendedor</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Flag title="Exigir documentos" on={mk.sellerRequireDocs} onChange={v => set({ sellerRequireDocs: v })}>
            <p>Ligado: a solicitação pede envio de documento (RG/CNH/selfie, até 3 arquivos). Só GMs veem os arquivos.</p>
          </Flag>
          <Flag title="Só conta com celular" on={mk.sellerRequireCellphone} onChange={v => set({ sellerRequireCellphone: v })}>
            <p>Ligado: só contas com celular vinculado conseguem pedir para vender.</p>
          </Flag>
          <Flag title="Aceitar qualquer chave Pix" on={mk.sellerPixTypes === 'all'} onChange={v => set({ sellerPixTypes: v ? 'all' : 'cpf' })}>
            <p>Desligado: <b className="text-white/80">só chave CPF</b>. Ligado: CPF, CNPJ, e-mail, celular ou chave aleatória.</p>
          </Flag>
        </div>
        <p className="text-[11px] text-white/40 mt-3 flex items-center gap-1.5"><Info size={12} /> A aprovação de um GM é sempre obrigatória.</p>
      </div>

      {/* ─── DEPÓSITO / CUSTÓDIA ─── */}
      <div className="rmt-card rounded-2xl p-5">
        <h3 className="text-sm font-black text-white uppercase tracking-wider mb-4 flex items-center gap-2"><Vault size={15} className="rmt-gold" /> Depósito (custódia)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rounded-xl border border-white/[0.07] bg-black/20 p-4">
            <p className="text-sm font-black text-white">Char-cofre</p>
            {config.cofreCharName
              ? <p className="text-sm text-white mt-1"><b className="rmt-gold">{config.cofreCharName}</b> <span className="text-white/40 font-mono">· idx {config.cofreCharIdx}</span></p>
              : <p className="text-sm text-red-400 mt-1">Não definido</p>}
            <p className="text-[12px] text-white/55 mt-1">Personagem que recebe o ALZ dos vendedores pelo correio.</p>
            <button onClick={() => setCofreOpen(true)} className="mt-3 px-3 py-1.5 rounded-lg bg-[#f0c86f]/10 hover:bg-[#f0c86f]/20 rmt-gold text-xs font-bold cursor-pointer">
              {config.cofreCharName ? 'Trocar char-cofre' : 'Definir char-cofre'}
            </button>
          </div>
          <Flag title="Detecção de depósito" on={depositEnabled} onChange={setDepositEnabled}>
            <p>Ligado: a cada 60s a API lê o correio do char-cofre e credita na custódia de cada vendedor ativo.</p>
            <div className="mt-2"><LimitField label="Depósito mínimo sugerido (ALZ)" value={minDeposit} onChange={setMinDeposit} /></div>
          </Flag>
        </div>
      </div>

      {/* ─── LIMITES ─── */}
      <div className="rmt-card rounded-2xl p-5">
        <h3 className="text-sm font-black text-white uppercase tracking-wider mb-1">Limites por vendedor</h3>
        <p className="text-[11px] text-white/45 mb-3">0 = sem limite. Ao atingir, o vendedor some das ofertas até o limite renovar (dia=24h, semana=7d, mês=30d).</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <LimitField label="ALZ / dia" value={limits.limitAlzDay} onChange={(v) => setLimits(l => ({ ...l, limitAlzDay: v }))} />
          <LimitField label="ALZ / semana" value={limits.limitAlzWeek} onChange={(v) => setLimits(l => ({ ...l, limitAlzWeek: v }))} />
          <LimitField label="ALZ / mês" value={limits.limitAlzMonth} onChange={(v) => setLimits(l => ({ ...l, limitAlzMonth: v }))} />
          <LimitField label="Vendas / dia" value={limits.limitSalesDay} onChange={(v) => setLimits(l => ({ ...l, limitSalesDay: v }))} />
          <LimitField label="Vendas / semana" value={limits.limitSalesWeek} onChange={(v) => setLimits(l => ({ ...l, limitSalesWeek: v }))} />
          <LimitField label="Vendas / mês" value={limits.limitSalesMonth} onChange={(v) => setLimits(l => ({ ...l, limitSalesMonth: v }))} />
        </div>
        <p className="text-[11px] text-white/45 mt-4 mb-2">Faixa de preço permitida para o ALZ (R$ por 100kk) — 0 = sem limite.</p>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <LimitField label="Mínimo" step="0.01" value={itemCfg.alzPriceMinBrl} onChange={(v) => setItemCfg(c => ({ ...c, alzPriceMinBrl: v }))} />
          <LimitField label="Máximo" step="0.01" value={itemCfg.alzPriceMaxBrl} onChange={(v) => setItemCfg(c => ({ ...c, alzPriceMaxBrl: v }))} />
        </div>
      </div>

      {/* ITENS: secao "Venda de itens" (itemsEnabled + preco min/max de item), "Blacklist de itens"
          e "Limites de preço por item" ficaram comentadas no fim do arquivo (mercado so' de ALZ). */}

      <div className="sticky bottom-3 z-10">
        <button onClick={save} disabled={saving}
                className="rmt-btn-gold w-full md:w-auto px-8 py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2 cursor-pointer">
          {saving && <Loader2 size={14} className="animate-spin" />} Salvar configuração
        </button>
      </div>

      {cofreOpen && <CofreModal onClose={() => setCofreOpen(false)} onSaved={async (cfg) => { setConfig(cfg); setCofreOpen(false); }} />}
    </div>
  );
}

/* ════════════════════════════ PAGE ════════════════════════════ */

const TABS: { k: Tab; l: string; i: React.ReactNode }[] = [
  { k: 'overview', l: 'Visão geral', i: <LayoutDashboard size={14} /> },
  { k: 'apps', l: 'Solicitações', i: <FileCheck2 size={14} /> },
  { k: 'listings', l: 'Anúncios', i: <Store size={14} /> },
  { k: 'orders', l: 'Vendas', i: <ReceiptText size={14} /> },
  { k: 'sellers', l: 'Vendedores', i: <Users size={14} /> },
  { k: 'config', l: 'Configuração', i: <Settings2 size={14} /> },
];

export default function RmtPage() {
  const { notify } = useToast();
  const [tab, setTab] = useState<Tab>('overview');
  const [mk, setMk] = useState<RmtMarketConfig | null>(null);
  const [scanning, setScanning] = useState(false);
  const [pending, setPending] = useState(0);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    getRmtMarketConfig().then(setMk).catch(() => {});
    getRmtApplications('pending').then(r => setPending(r.length)).catch(() => {});
  }, [tab, nonce]);

  const doScan = async () => {
    setScanning(true);
    try {
      const r = await runRmtScan();
      if (!r.enabled) notify('warning', 'Detecção desativada ou cofre não definido');
      else notify('success', `Scan: ${r.processed} depósito(s) · ${fmt(r.totalAlz)} ALZ`);
      setNonce(n => n + 1);
    } catch (e: any) { notify('error', errMsg(e, 'Erro no scan')); }
    finally { setScanning(false); }
  };

  return (
    <div className="space-y-5">
      <div className="rmt-hero rounded-2xl p-5 md:p-6">
        <div className="relative z-10 flex flex-wrap items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#f0c86f]/10 border border-[#f0c86f]/30 flex items-center justify-center">
            <HandCoins size={24} className="rmt-gold" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-2xl md:text-3xl font-black text-white">Mercado de ALZ</h1>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {mk && <>
                <span className={`rmt-pill ${mk.testMode ? 'bg-sky-500/15 text-sky-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                  <FlaskConical size={11} /> {mk.testMode ? 'Modo teste · só GM' : 'Aberto a todos'}
                </span>
                <span className={`rmt-pill ${mk.sandboxMode ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                  <QrCode size={11} /> {mk.sandboxMode ? 'Sandbox · grátis' : 'Pix real'}
                </span>
                <span className="rmt-pill bg-white/5 text-white/60"><Percent size={11} /> comissão {mk.commissionPct}%</span>
              </>}
            </div>
          </div>
          <button onClick={doScan} disabled={scanning}
                  className="px-4 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/80 text-sm font-bold flex items-center gap-2 cursor-pointer disabled:opacity-50">
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Escanear depósitos
          </button>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-white/[0.06]">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  className={`px-4 py-2.5 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 whitespace-nowrap cursor-pointer
                              ${tab === t.k ? 'text-[#f0c86f] shadow-[inset_0_-2px_0_#f0c86f]' : 'text-white/45 hover:text-white'}`}>
            {t.i}{t.l}
            {t.k === 'apps' && pending > 0 && <span className="ml-1 px-1.5 rounded-full bg-amber-500 text-[#1a1206] text-[10px]">{pending}</span>}
          </button>
        ))}
      </div>

      <div key={`${tab}-${nonce}`}>
        {tab === 'overview' && <OverviewTab go={setTab} />}
        {tab === 'apps' && <ApplicationsTab />}
        {tab === 'listings' && <ListingsTab />}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'sellers' && <SellersTab />}
        {tab === 'config' && <ConfigTab />}
      </div>
    </div>
  );
}


// ----------------------------------------------------------------- Cofre modal

function CofreModal({
  onClose, onSaved,
}: {
  onClose: () => void;
  onSaved: (cfg: RmtConfig) => Promise<void>;
}) {
  const { notify } = useToast();
  const [term, setTerm] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<RmtCharSearch[]>([]);

  const search = async () => {
    if (term.trim().length < 2) { notify('error', 'Digite ao menos 2 caracteres'); return; }
    setBusy(true);
    try { setResults(await searchRmtChar(term.trim())); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  const pick = async (c: RmtCharSearch) => {
    try {
      const cfg = await setRmtCofre(c.characterIdx);
      notify('success', `Char-cofre definido: ${c.name}`);
      await onSaved(cfg);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Definir char-cofre</h3>
          <button onClick={onClose} className="text-white/55 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Nome do personagem ou CharacterIdx"
            className="flex-1 bg-[#0d141b] border border-white/[0.06] rounded-lg
                       px-3 py-2 text-sm text-white"
          />
          <button
            onClick={search}
            disabled={busy}
            className="px-4 rounded-lg btn-primary
                       font-bold text-sm cursor-pointer disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : 'Buscar'}
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {results.map(c => (
            <button
              key={c.characterIdx}
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 rounded-lg bg-white/[0.03]
                         hover:bg-amber-500/10 border border-white/[0.06]
                         hover:border-amber-400/40 transition cursor-pointer"
            >
              <span className="text-sm font-bold text-white">{c.name}</span>
              <span className="text-[11px] text-white/45 font-mono">
                {' '}· idx {c.characterIdx} · conta {c.userNum}
                {c.online && <span className="text-emerald-400"> · online</span>}
              </span>
            </button>
          ))}
          {!busy && results.length === 0 && (
            <p className="text-xs text-white/40 text-center py-4">
              Busque um personagem para defini-lo como cofre.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------- Seller editor

function SellerEditor({
  seller, onClose, onSaved,
}: {
  seller: RmtSeller;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { notify } = useToast();
  const [status, setStatus] = useState(seller.status);
  const [docStatus, setDocStatus] = useState(seller.docStatus);
  const [notes, setNotes] = useState(seller.notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateRmtSeller(seller.userNum, { status, docStatus, notes: notes || undefined });
      notify('success', 'Vendedor atualizado');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const pill = (active: boolean) =>
    `flex-1 px-3 py-2 rounded-lg text-xs font-bold transition cursor-pointer border ${
      active ? 'bg-amber-500/15 border-amber-400/40 text-amber-400'
             : 'bg-white/[0.03] border-white/[0.06] text-white/65 hover:bg-white/[0.06]'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-white">Editar vendedor</h3>
            <p className="text-[11px] text-white/55 font-mono mt-1">
              {seller.accountId} · UserNum {seller.userNum}
            </p>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <label className="text-[11px] text-white/55 mb-1.5 block">Status</label>
        <div className="flex gap-2 mb-4">
          <button className={pill(status === 'active')} onClick={() => setStatus('active')}>
            Ativo
          </button>
          <button className={pill(status === 'suspended')} onClick={() => setStatus('suspended')}>
            Suspenso
          </button>
        </div>

        <label className="text-[11px] text-white/55 mb-1.5 block">Documentação</label>
        <div className="flex gap-2 mb-4">
          <button className={pill(docStatus === 'pending')} onClick={() => setDocStatus('pending')}>
            Pendente
          </button>
          <button className={pill(docStatus === 'approved')} onClick={() => setDocStatus('approved')}>
            Aprovada
          </button>
          <button className={pill(docStatus === 'rejected')} onClick={() => setDocStatus('rejected')}>
            Rejeitada
          </button>
        </div>

        <label className="text-[11px] text-white/55 mb-1 block">Notas (opcional)</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={255}
          className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                     px-3 py-2 text-sm text-white mb-5"
        />

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75
                       hover:bg-white/[0.08] transition cursor-pointer text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400
                       text-[#0d141b] font-bold text-sm transition cursor-pointer
                       disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ITENS — COMENTADO (mercado so' de ALZ por enquanto). JSX original da tela
   antiga, pra religar: estados blacklist/priceLimits/blItemId..., handlers
   addBl/delBl/savePl/delPl (getRmtBlacklist, addRmtBlacklist, removeRmtBlacklist,
   getRmtPriceLimits, upsertRmtPriceLimit, removeRmtPriceLimit) e o toggle
   itemCfg.itemsEnabled + itemPriceMinBrl/itemPriceMaxBrl no ConfigTab.
   (os "/ *" e "* /" abaixo eram comentarios JSX — separados pra nao fechar este bloco)

            {/ * Venda de itens * /}
            <div className="mt-5 bg-[#0d141b] border border-white/[0.06] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs font-bold text-white/70 uppercase tracking-wider">
                    Venda de itens
                  </p>
                  <p className="text-[11px] text-white/45">
                    Liga o marketplace de itens (depósito + venda).
                  </p>
                </div>
                <button
                  onClick={() => setItemCfg(c => ({ ...c, itemsEnabled: !c.itemsEnabled }))}
                  className={`relative w-11 h-6 rounded-full transition shrink-0
                              ${itemCfg.itemsEnabled ? 'bg-emerald-500' : 'bg-white/15'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all
                                    ${itemCfg.itemsEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
              <p className="text-[11px] text-white/45 mb-2">
                Preços globais em R$ — 0 = sem limite.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <LimitField label="Alz mín /100kk" value={itemCfg.alzPriceMinBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, alzPriceMinBrl: v }))} />
                <LimitField label="Alz máx /100kk" value={itemCfg.alzPriceMaxBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, alzPriceMaxBrl: v }))} />
                <LimitField label="Item mín" value={itemCfg.itemPriceMinBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, itemPriceMinBrl: v }))} />
                <LimitField label="Item máx" value={itemCfg.itemPriceMaxBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, itemPriceMaxBrl: v }))} />
              </div>
            </div>

            <button
              onClick={saveConfig}
              disabled={savingCfg}
              className="mt-4 px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400
                         text-[#0d141b] font-bold text-sm transition cursor-pointer
                         disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingCfg && <Loader2 size={14} className="animate-spin" />}
              Salvar configuração
            </button>
          </div>

          {/ * Blacklist de itens * /}
          <div className="surface-card rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Ban size={15} className="text-red-400" />
              <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
                Blacklist de itens
              </p>
            </div>
            <p className="text-[11px] text-white/45 mb-3">
              Itens proibidos no marketplace. Option vazia = qualquer option do item.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <input value={blItemId} onChange={(e) => setBlItemId(e.target.value)}
                     placeholder="ID do item" type="number"
                     className="w-32 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={blOption} onChange={(e) => setBlOption(e.target.value)}
                     placeholder="Option (opcional)" type="number"
                     className="w-40 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={blReason} onChange={(e) => setBlReason(e.target.value)}
                     placeholder="Motivo"
                     className="flex-1 min-w-[140px] bg-[#0d141b] border border-white/[0.06]
                                rounded-lg px-3 py-2 text-sm text-white" />
              <button onClick={addBl}
                      className="px-4 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400
                                 text-sm font-bold transition cursor-pointer">
                Bloquear
              </button>
            </div>
            {blacklist.length === 0 ? (
              <p className="text-xs text-white/35 py-2">Nenhum item bloqueado.</p>
            ) : (
              <div className="border border-white/[0.06] rounded-lg overflow-hidden">
                {blacklist.map(b => (
                  <div key={b.blacklistId}
                       className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.04]
                                  last:border-0 text-sm">
                    <span className="font-mono text-white/80">#{b.itemId}</span>
                    <span className="text-[11px] text-white/45">
                      {b.itemOption != null ? `option ${b.itemOption}` : 'qualquer option'}
                    </span>
                    {b.reason && (
                      <span className="text-[11px] text-white/55 italic truncate">"{b.reason}"</span>
                    )}
                    <button onClick={() => delBl(b.blacklistId)}
                            className="ml-auto p-1.5 rounded bg-white/[0.04] hover:bg-red-500/20
                                       text-white/50 hover:text-red-400 cursor-pointer">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/ * Limites de preço por item * /}
          <div className="surface-card rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Tag size={15} className="text-amber-400" />
              <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
                Limites de preço por item
              </p>
            </div>
            <p className="text-[11px] text-white/45 mb-3">
              Sobrepõe o limite global de item. 0 = sem teto. Salvar com um ID
              já cadastrado atualiza.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <input value={plItemId} onChange={(e) => setPlItemId(e.target.value)}
                     placeholder="ID do item" type="number"
                     className="w-32 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={plMin} onChange={(e) => setPlMin(e.target.value)}
                     placeholder="Mín R$" type="number"
                     className="w-28 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={plMax} onChange={(e) => setPlMax(e.target.value)}
                     placeholder="Máx R$" type="number"
                     className="w-28 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <button onClick={savePl}
                      className="px-4 rounded-lg btn-primary
                                 text-sm font-bold transition cursor-pointer">
                Salvar
              </button>
            </div>
            {priceLimits.length === 0 ? (
              <p className="text-xs text-white/35 py-2">Nenhum limite por item.</p>
            ) : (
              <div className="border border-white/[0.06] rounded-lg overflow-hidden">
                {priceLimits.map(p => (
                  <div key={p.itemId}
                       className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.04]
                                  last:border-0 text-sm">
                    <span className="font-mono text-white/80">#{p.itemId}</span>
                    <span className="text-[11px] text-white/55">
                      R$ {p.minPriceBrl.toFixed(2)} —{' '}
                      {p.maxPriceBrl > 0 ? `R$ ${p.maxPriceBrl.toFixed(2)}` : 'sem teto'}
                    </span>
                    <button onClick={() => delPl(p.itemId)}
                            className="ml-auto p-1.5 rounded bg-white/[0.04] hover:bg-red-500/20
                                       text-white/50 hover:text-red-400 cursor-pointer">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
   ═══════════════════════════════════════════════════════════════════════════ */
